// CB:BEGIN add-verified-contractor-modal
import React, { useState } from 'react';
import { useApi } from '@/lib/useApi';
import { FaTimes } from 'react-icons/fa';

interface AddVerifiedContractorModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingContractors: string[];
}

export const AddVerifiedContractorModal: React.FC<AddVerifiedContractorModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onSuccess,
  existingContractors
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [contractorName, setContractorName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    const trimmedName = contractorName.trim();
    if (!trimmedName) {
      setError('Contractor name is required');
      return;
    }

    // Check for duplicates
    if (existingContractors.some(name => name.toLowerCase() === trimmedName.toLowerCase())) {
      setError('This contractor is already in the list');
      return;
    }

    setLoading(true);

    try {
      // Get current project to preserve existing metadata
      const project = await api.get(`/api/admin/projects/${projectId}`);
      
      // Update metadata with new verified contractor
      const currentMetadata = project.metadata && typeof project.metadata === 'object' 
        ? project.metadata 
        : {};
      
      const currentVerifiedContractors = Array.isArray(currentMetadata.verifiedContractors)
        ? currentMetadata.verifiedContractors
        : [];

      const updatedVerifiedContractors = [...currentVerifiedContractors, trimmedName];

      // Update project metadata
      await api.put(`/api/admin/projects/${projectId}`, {
        metadata: {
          ...currentMetadata,
          verifiedContractors: updatedVerifiedContractors
        }
      });

      // Reset form and close
      setContractorName('');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error adding verified contractor:', err);
      setError(err instanceof Error ? err.message : 'Failed to add verified contractor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setContractorName('');
      setError(null);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '500px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
            Add Verified Contractor
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.color = '#6b7280';
            }}
          >
            <FaTimes style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#991b1b',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Contractor Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={contractorName}
              onChange={(e) => setContractorName(e.target.value)}
              placeholder="Enter contractor name"
              disabled={loading}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#2563eb';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '4px',
              marginBottom: 0
            }}>
              This contractor will be added to the dropdown list for bid creation.
            </p>
          </div>

          {/* Existing Contractors List */}
          {existingContractors.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Existing Verified Contractors
              </label>
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {existingContractors.map((name, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '6px 0',
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: index < existingContractors.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !contractorName.trim()}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                background: loading || !contractorName.trim() ? '#9ca3af' : '#2563eb',
                color: 'white',
                cursor: loading || !contractorName.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading && contractorName.trim()) {
                  e.currentTarget.style.background = '#1d4ed8';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && contractorName.trim()) {
                  e.currentTarget.style.background = '#2563eb';
                }
              }}
            >
              {loading ? 'Adding...' : 'Add Contractor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// CB:END add-verified-contractor-modal

